import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useToast } from '@chakra-ui/react';
// import { useNavigate } from 'react-router-dom'; // Pas utilisé dans le toast finalement

export interface Notification {
  id: string;
  type: 'project_import_success' | 'project_import_failed' | 'inventory_sync_success' | string;
  title: string;
  message: string;
  link?: string;
  read: boolean;
  createdAt: any;
  userId: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  markAsRead: (ids: string[]) => Promise<void>;
  refreshNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { currentUser, getToken } = useAuth();
  const toast = useToast();
  // const navigate = useNavigate();

  // Polling interval (30s)
  useEffect(() => {
    if (!currentUser) return;

    fetchNotifications(); // Initial fetch

    const interval = setInterval(() => {
      fetchNotifications(true); // Silent fetch
    }, 30000);

    return () => clearInterval(interval);
  }, [currentUser]);

  const fetchNotifications = async (silent = false) => {
    try {
      const token = await getToken();
      if (!token) return;

      const response = await fetch('/.netlify/functions/notifications', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const newNotifications: Notification[] = data.notifications || [];
        
        setNotifications(prev => {
          // Détection des nouvelles notifications pour les Toasts
          if (silent && prev.length > 0) {
            const latestPrevDate = prev[0]?.createdAt?._seconds || 0;
            const newItems = newNotifications.filter(n => {
                const newDate = n.createdAt?._seconds || 0;
                return newDate > latestPrevDate && !n.read;
            });

            newItems.forEach(n => {
              toast({
                title: n.title,
                description: n.message,
                status: n.type.includes('failed') ? 'error' : 'success',
                duration: 5000,
                isClosable: true,
                position: 'top-right',
              });
            });
          }
          return newNotifications;
        });
      }
    } catch (error) {
      console.error("Failed to fetch notifications", error);
    }
  };

  const markAsRead = async (ids: string[]) => {
    try {
      // Optimistic update
      setNotifications(prev => prev.map(n => 
        ids.includes(n.id) ? { ...n, read: true } : n
      ));

      const token = await getToken();
      await fetch('/.netlify/functions/notifications', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ notificationIds: ids })
      });
    } catch (error) {
      console.error("Failed to mark notifications as read", error);
      // Revert optimistic update ideally, but skipping for simplicity
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, markAsRead, refreshNotifications: () => fetchNotifications() }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
